import { useState, useEffect } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import { Loader2, TrendingUp, TrendingDown, Minus, BarChart3, AlertTriangle } from "lucide-react";
import SparklineSVG from "./SparklineSVG";

const CITY_TYPE_LABELS = {
  dense_urban: "Dense Urban (500+)",
  suburban: "Suburban (100-499)",
  small_town: "Small Town (20-99)",
  rural: "Rural (<20)",
};

const TREND_DOT = {
  green: "bg-emerald-500",
  orange: "bg-orange-500",
  red: "bg-red-500",
  gray: "bg-slate-300",
};

const TREND_LABEL = {
  improving: "Improving",
  stable: "Stable",
  degrading: "Degrading",
  critically_degraded: "Critically degraded",
  no_data: "No data",
};

export default function SweepPerformancePanel() {
  const [report, setReport] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [expandedLogs, setExpandedLogs] = useState({}); // key: `${source}|${city_type}` → { loading, rows }

  const fetchReport = async () => {
    try {
      const [legacy, rolling] = await Promise.all([
        axios.get(`${API}/admin/sweep-performance`),
        axios.get(`${API}/admin/sweep-performance-analytics`),
      ]);
      setReport(legacy.data);
      setAnalytics(rolling.data);
      setLastRefreshedAt(Date.now());
    } catch { setReport(null); setAnalytics(null); }
    finally { setLoading(false); }
  };

  const toggleLogs = async (sourceName, cityType) => {
    const key = `${sourceName}|${cityType || "all"}`;
    if (expandedLogs[key]) {
      const next = { ...expandedLogs };
      delete next[key];
      setExpandedLogs(next);
      return;
    }
    setExpandedLogs((p) => ({ ...p, [key]: { loading: true, rows: [] } }));
    try {
      const params = { source_name: sourceName, limit: 20 };
      if (cityType) params.city_type = cityType;
      const res = await axios.get(`${API}/admin/sweep-performance-analytics/logs`, { params });
      setExpandedLogs((p) => ({ ...p, [key]: { loading: false, rows: res.data.rows || [] } }));
    } catch {
      setExpandedLogs((p) => ({ ...p, [key]: { loading: false, rows: [], error: true } }));
    }
  };

  useEffect(() => { fetchReport(); }, []);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;
  if (!report) return <p className="text-sm text-slate-400 text-center py-4">No performance data yet</p>;

  const avgEff = report.average_efficiency || 0;
  const hasData = report.total_records > 0;

  return (
    <div className="space-y-4" data-testid="sweep-performance-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={fetchReport} lastRefreshedAt={lastRefreshedAt} testId="sweep-perf-refresh" /></div>
      {/* Summary bar */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{report.total_records} sweep runs recorded</span>
        {avgEff > 0 && <span>Avg efficiency: <b className="text-slate-700">{avgEff.toFixed(2)}</b> fields/call</span>}
      </div>

      {/* ── Part 19 — Rolling Window Analytics (7d / 30d trends) ── */}
      {analytics && analytics.total_records > 0 && (
        <div data-testid="rolling-analytics">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-600">Rolling Trend (last 7 days)</p>
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
              Overall
              <span className={`w-2.5 h-2.5 rounded-full ${TREND_DOT[analytics.overall_status] || TREND_DOT.gray}`} data-testid="analytics-overall-dot" />
              <b className="text-slate-700">{analytics.overall_status}</b>
            </span>
          </div>
          {(analytics.windows?.["7d"]?.rows || []).length === 0 ? (
            <p className="text-xs text-slate-400 italic">Not enough data yet — trend activates after 3 runs per source.</p>
          ) : (
            <div className="space-y-1.5">
              {analytics.windows["7d"].rows.map((r) => {
                const isDegraded = r.trend === "degrading" || r.trend === "critically_degraded";
                const key = `${r.source_name}|${r.city_type || "all"}`;
                const expanded = expandedLogs[key];
                return (
                  <div key={r.source_name} data-testid={`trend-row-${r.source_name}`}>
                    <div className="flex items-center gap-2 text-xs bg-white border border-slate-100 rounded-lg px-3 py-2">
                      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${TREND_DOT[r.status] || TREND_DOT.gray}`} />
                      <span className="font-semibold text-slate-700 w-28 truncate">{r.source_name}</span>
                      <span className="text-slate-500 w-24">{TREND_LABEL[r.trend] || r.trend}</span>
                      <span className="flex-shrink-0" data-testid={`sparkline-wrap-${r.source_name}`}>
                        <SparklineSVG
                          points={(r.daily_points || []).map((p) => p.efficiency)}
                          testId={`sparkline-${r.source_name}`}
                        />
                      </span>
                      <span className="text-slate-400 flex-1 text-right">
                        {r.pct_change !== null && r.pct_change !== undefined ? (
                          <span className={r.pct_change > 0 ? "text-emerald-600" : r.pct_change < 0 ? "text-red-600" : "text-slate-400"}>
                            {r.pct_change > 0 ? "+" : ""}{r.pct_change}%
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </span>
                      <span className="text-slate-400 w-16 text-right">{r.runs_in_window} runs</span>
                      <span className="text-slate-400 w-16 text-right">{r.cities_in_window} cities</span>
                      {isDegraded && (
                        <button
                          type="button"
                          onClick={() => toggleLogs(r.source_name, r.city_type)}
                          className="text-[10px] text-sky-600 hover:text-sky-700 hover:underline font-medium ml-1 whitespace-nowrap"
                          data-testid={`jump-to-logs-${r.source_name}`}
                        >
                          {expanded ? "Hide logs" : "Jump to logs ›"}
                        </button>
                      )}
                    </div>
                    {expanded && (
                      <div className="ml-5 mt-1 mb-1 border-l-2 border-sky-200 pl-3" data-testid={`logs-${r.source_name}`}>
                        {expanded.loading ? (
                          <div className="text-[11px] text-slate-400 py-1.5">Loading raw logs…</div>
                        ) : expanded.error ? (
                          <div className="text-[11px] text-red-500 py-1.5">Failed to load logs</div>
                        ) : expanded.rows.length === 0 ? (
                          <div className="text-[11px] text-slate-400 py-1.5 italic">No log rows found for this source.</div>
                        ) : (
                          <div className="text-[10px] text-slate-500 space-y-0.5 max-h-48 overflow-y-auto">
                            <div className="grid grid-cols-6 gap-2 text-slate-400 font-semibold pb-1 border-b border-slate-100">
                              <span>Ran</span><span>City</span><span>Tier</span><span>Fill</span><span>Eff</span><span>Time</span>
                            </div>
                            {expanded.rows.map((lg, i) => (
                              <div key={i} className="grid grid-cols-6 gap-2 py-0.5">
                                <span className="text-slate-400 truncate" title={lg.ran_at}>{(lg.ran_at || "").slice(5, 16).replace("T", " ")}</span>
                                <span className="text-slate-600 truncate" title={lg.city}>{lg.city}</span>
                                <span className="text-slate-400">{lg.city_type}</span>
                                <span>{((lg.fill_rate || 0) * 100).toFixed(0)}%</span>
                                <span>{(lg.efficiency_score || 0).toFixed(2)}</span>
                                <span className="text-slate-400">{(lg.time_seconds || 0).toFixed(1)}s</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {analytics.windows?.["7d"]?.top_degraded?.length > 0 && (
            <div className="mt-3 p-2.5 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2" data-testid="top-degraded-callout">
              <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-orange-800">
                <strong>Top degraded this week:</strong>{" "}
                {analytics.windows["7d"].top_degraded.map(d => d.source_name).join(", ")}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Overall source performance */}
      {hasData && Object.keys(report.overall).length > 0 && (
        <div data-testid="overall-performance">
          <p className="text-xs font-semibold text-slate-600 mb-2">Overall Source Performance</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.entries(report.overall).map(([source, metrics]) => {
              const isAbove = metrics.efficiency_score > avgEff;
              const isBelow = metrics.efficiency_score < avgEff * 0.8;
              return (
                <div
                  key={source}
                  className={`rounded-xl border p-2.5 text-center ${
                    isAbove ? "bg-emerald-50 border-emerald-200" : isBelow ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"
                  }`}
                  data-testid={`source-card-${source}`}
                >
                  <div className="flex items-center justify-center gap-1 mb-1">
                    {isAbove ? <TrendingUp className="w-3 h-3 text-emerald-500" /> :
                     isBelow ? <TrendingDown className="w-3 h-3 text-red-500" /> :
                     <Minus className="w-3 h-3 text-slate-400" />}
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{source}</span>
                  </div>
                  <p className="text-sm font-bold text-slate-700">{(metrics.fill_rate * 100).toFixed(1)}%</p>
                  <p className="text-[10px] text-slate-400">fill rate</p>
                  <p className="text-xs text-slate-500 mt-0.5">{metrics.efficiency_score.toFixed(2)} eff</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* By city type breakdown */}
      {hasData && Object.keys(report.by_city_type).length > 0 && (
        <div data-testid="city-type-breakdown">
          <p className="text-xs font-semibold text-slate-600 mb-2">Performance by City Type</p>
          <div className="space-y-3">
            {Object.entries(report.by_city_type).map(([cityType, sources]) => (
              <div key={cityType} className="bg-slate-50 rounded-xl border border-slate-100 p-3">
                <p className="text-[11px] font-semibold text-slate-500 mb-2">
                  {CITY_TYPE_LABELS[cityType] || cityType}
                </p>
                <div className="space-y-1">
                  {sources.map((s) => (
                    <div key={s.source_name} className="flex items-center justify-between text-xs" data-testid={`perf-row-${cityType}-${s.source_name}`}>
                      <span className="text-slate-600 font-medium w-24">{s.source_name}</span>
                      <div className="flex-1 mx-2 bg-slate-200 rounded-full h-1.5">
                        <div
                          className="h-full rounded-full bg-amber-400"
                          style={{ width: `${Math.min(s.fill_rate * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-slate-500 w-12 text-right">{(s.fill_rate * 100).toFixed(0)}%</span>
                      <span className="text-slate-400 w-16 text-right">{s.efficiency_score.toFixed(1)} eff</span>
                      <span className="text-slate-400 w-16 text-right">{s.city_count} cities</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No data state */}
      {!hasData && (
        <div className="text-center py-6" data-testid="no-performance-data">
          <BarChart3 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No sweep performance data yet</p>
          <p className="text-xs text-slate-300 mt-1">Data will appear after city enrichment sweeps run</p>
        </div>
      )}
    </div>
  );
}
